import {
  Deposit,
  DepositCondition,
  DepositConditionType,
  DepositPayable,
  DepositType,
  FormCondition,
  FormConditionCondition,
  FormConditionConditionType,
  useStaffGetSpecialConditions,
} from '@urbanx/agx.campaigns.client'
import {
  AgxDropdown,
  AgxColumn,
  AgxDatePicker,
} from '@urbanx/agx.ui-components2'
import dayjs from 'dayjs'
import { useSelectedContract } from 'hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormConditionDateOptions } from './useFormConditionDateOptions'
import { useSpecialConditionsOptions } from './useSpecialConditionsOptions'

interface FormConditionOnDateProps {
  id: string
  defaultValue?: FormCondition | Deposit
  onSpecificDateOptions: FormConditionCondition[]
  onValueChange: (_condition: FormCondition | Deposit) => void
  conditionTypeOverride?: FormConditionConditionType | DepositConditionType
  depositType?: DepositType
}

export function FormConditionOnDate({
  id,
  defaultValue,
  onSpecificDateOptions,
  onValueChange,
  conditionTypeOverride,
  depositType,
}: FormConditionOnDateProps) {
  const { contract, campaignId } = useSelectedContract()
  const [value, setValue] = useState<(FormCondition | Deposit) | undefined>(
    defaultValue
  )
  const specificDateOptions = useFormConditionDateOptions(
    onSpecificDateOptions,
    true,
    depositType
  )

  const [specificDateCondition, setSpecificDateCondition] = useState<
    FormConditionCondition | DepositCondition | undefined
  >(() => defaultValue?.condition as FormConditionCondition | DepositCondition)

  const { data: specialConditions } = useStaffGetSpecialConditions(
    campaignId ?? '',
    contract?.contractId || ''
  )

  useEffect(() => {
    setValue(
      (prev) =>
        ({
          ...prev,
          condition: (specificDateCondition ??
            undefined) as FormConditionCondition &
            DepositPayable &
            DepositCondition,
          ...(conditionTypeOverride && {
            conditionType: conditionTypeOverride,
          }),
          // When OnDate is selected (contract/unconditional date), set conditionType appropriately
          ...(specificDateCondition === DepositCondition.OnDate &&
            !conditionTypeOverride && {
              conditionType:
                depositType === DepositType.Balance
                  ? DepositConditionType.Unconditional
                  : DepositConditionType.Contract,
            }),
        }) as FormCondition | Deposit
    )
  }, [specificDateCondition, conditionTypeOverride, depositType])

  useEffect(() => {
    // Only update if the current condition is not in the available options
    const isCurrentConditionAvailable = specificDateOptions.find(
      (option) => option.value === specificDateCondition
    )

    if (isCurrentConditionAvailable || !specificDateCondition) return

    // If current condition is not available, select the first valid option
    const updateTo = specificDateOptions.find((option) => option.value !== '')
      ?.value as FormConditionCondition | DepositCondition

    if (updateTo && updateTo !== specificDateCondition) {
      setSpecificDateCondition(updateTo)
    }
  }, [specificDateOptions, specificDateCondition])

  useEffect(() => {
    if (value?.condition) {
      onValueChange(value)
    }
  }, [value])

  const specialConditionsOptions = useSpecialConditionsOptions(
    specialConditions?.renderedConditions ?? []
  )

  const selectedSpecialCondition = useMemo(() => {
    if (!value) return ''

    return value?.specialConditionId || ''
  }, [value])

  const onSelectedSpecialConditionChange = useCallback(
    (value: string) => {
      const foundId = value === '' ? undefined : value

      setValue((prev) => ({
        ...prev,
        specialConditionId: foundId,
      }))
    },
    [specialConditions]
  )

  const financeSpecificDateDefaultValue = useMemo(() => {
    return value?.referenceDate
      ? dayjs(value?.referenceDate).format('YYYY-MM-DD')
      : ''
  }, [value?.referenceDate])

  return (
    <AgxColumn gap="spacing-lg" fill>
      <AgxDropdown
        id={`${id}-ConditionFromOptions`}
        options={specificDateOptions}
        selected={specificDateCondition ?? ''}
        onChange={(value) => {
          setSpecificDateCondition(
            value as FormConditionCondition | DepositCondition
          )
        }}
        hideOptionalText
        noHeader
      />

      {specificDateCondition === FormConditionCondition.OnSpecificDate && (
        <AgxDatePicker
          id={`${id}-FinanceOnSpecificDate`}
          value={financeSpecificDateDefaultValue ?? undefined}
          noHeader
          variant="compact"
          stretch
          onChange={(d: Date | null) => {
            setValue((prev) => ({
              ...prev,
              referenceDate: d ? d : undefined,
            }))
          }}
          optionalText=""
        />
      )}

      {specificDateCondition ===
        FormConditionCondition.UponSatisfactionOfSpecialCondition && (
        <AgxDropdown
          id={`${id}-SpecialConditionDropdown`}
          options={specialConditionsOptions}
          selected={selectedSpecialCondition}
          onChange={onSelectedSpecialConditionChange}
          hideOptionalText
          required
        />
      )}
    </AgxColumn>
  )
}
